import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "影视", to: "/category/movie" },
  { label: "小说", to: "/category/novel" },
  { label: "资讯", to: "/category/news" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    setMenuOpen(false);
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
      isActive
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50/80 backdrop-blur"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-black text-lg">
            17
          </span>
          <span className="font-heading font-black text-xl tracking-tight text-foreground-950">
            17C<span className="text-accent-500">.</span>COM
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 h-10 px-3 rounded-lg bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视 / 小说 / 资讯"
              className="w-44 lg:w-56 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
            />
          </form>
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen(true)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 cursor-pointer"
        >
          <i className="ri-menu-3-line text-2xl"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 top-0 z-50 bg-background-50 animate-fade-in">
          <div className="h-16 px-4 flex items-center justify-between border-b border-background-200">
            <span className="font-heading font-black text-xl text-foreground-950">
              17C<span className="text-accent-500">.</span>COM
            </span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 h-11 px-3 rounded-lg bg-background-100 border border-background-200"
            >
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视 / 小说 / 资讯"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
            </form>
            <nav className="flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-lg text-base font-medium cursor-pointer ${
                      isActive
                        ? "bg-primary-50 text-primary-600"
                        : "text-foreground-800 hover:bg-background-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}